import React from "react";

import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";

import useRoutePaths from "hooks/useRoutePaths";
import { Authentication as BaseAuthentication } from "libs/authentication";
import { LOGIN, HOME } from "routers/routes";

/**
 * This component is a wrapper to use mainly in routers to manage pages access authorizations.
 * That allow us to filter users and give access to pages only for authenticated or not authenticated user for example.
 * If the access is refused, then we allow to define an action to trigger (to make a redirection dor example).
 * By default, the user is redirect either to /login page (if he must be authenticated) or /home (if he must not be).
 * But later, we could add other kind of authorization (user groups...).
 * The rule and the action to do when the rule is not fulfilled is provided by props to this component.
 * At the end this filter check whether the user is authorized according to the authentication rules defined
 * by the props (internal context about whether the user is authenticated (logged in) or not).
 */
const RouteFilterAuthentication = ({
  authenticatedAuthorized,
  notAuthenticatedAuthorized,
  onNotAuthorized,
  children,
}) => {
  const routePaths = useRoutePaths();

  const onNotAuthorizedValue = React.useCallback(
    () => {
      if (onNotAuthorized) {
        return onNotAuthorized();
      }
      if (authenticatedAuthorized) {
        return <Navigate to={routePaths[LOGIN]()} />;
      }
      if (notAuthenticatedAuthorized) {
        return <Navigate to={routePaths[HOME]()} replace />;
      }
      return null;
    },
    [authenticatedAuthorized, notAuthenticatedAuthorized, onNotAuthorized, routePaths],
  );

  return (
    <BaseAuthentication
      authenticatedAuthorized={authenticatedAuthorized}
      notAuthenticatedAuthorized={notAuthenticatedAuthorized}
      onNotAuthorized={onNotAuthorizedValue}
    >
      {children}
    </BaseAuthentication>
  );
};

RouteFilterAuthentication.propTypes = {
  /**
   * Whether the user is authorized if he is authenticated.
   * If true, authenticated users are allowed to access this route.
   */
  authenticatedAuthorized: PropTypes.bool,
  /**
   * Whether the user is authorized if he is not authenticated.
   * If true, unauthenticated users are allowed to access this route.
   */
  notAuthenticatedAuthorized: PropTypes.bool,
  /**
   * The action to trigger when the authentication rule is not fulfilled.
   * If null, by default we redirect to the login route if user not authenticated and not authorized,
   * and to the home route if the user is authenticated but not authorized.
   */
  onNotAuthorized: PropTypes.func,
  /**
   * The components to render when the authentication rules defined are fulfilled.
   */
  children: PropTypes.node,
};

RouteFilterAuthentication.defaultProps = {
  authenticatedAuthorized: false,
  notAuthenticatedAuthorized: false,
  onNotAuthorized: null,
  children: null,
};

export default RouteFilterAuthentication;
